import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { copyToClipboard } from '../utils/clipboard';
import { Copy } from 'lucide-react';
import { theme } from '../styles/theme';

const Container = styled.div`
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.1) 100%);
  backdrop-filter: blur(24px);
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 20px;
  box-shadow: 0 20px 40px rgba(99, 102, 241, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.2);
  overflow: hidden;
  max-width: 500px;
  width: 100%;
  margin: 20px auto;
  font-family: ${theme.typography.fontFamily};
`;

const Header = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.4);
  backdrop-filter: blur(10px);
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Title = styled.h3`
  font-size: ${theme.typography.sizes.base};
  font-weight: ${theme.typography.weights.bold};
  color: white;
  margin: 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
`;

const StatusIndicators = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const StatusIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: ${theme.typography.sizes.xs};
  color: rgba(255, 255, 255, 0.8);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
`;

const StatusDot = styled.div<{ $color: string }>`
  width: 8px;
  height: 8px;
  background: ${props => props.$color};
  border-radius: 50%;
  animation: pulse 1.5s ease-in-out infinite;
  
  @keyframes pulse {
    0% { opacity: 1; }
    50% { opacity: 0.5; }
    100% { opacity: 1; }
  }
`;

const TranscriptArea = styled.div`
  height: 300px;
  overflow-y: auto;
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${theme.colors.glass.background};
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${theme.colors.glass.border};
    border-radius: 3px;
  }
`;

const EmptyMessage = styled.div`
  text-align: center;
  color: ${theme.colors.text.muted};
  font-size: ${theme.typography.sizes.sm};
  padding: 40px 20px;
`;

const TranscriptEntry = styled.div<{ $speaker: 'user' | 'agent' }>`
  display: flex;
  justify-content: ${props => props.$speaker === 'user' ? 'flex-end' : 'flex-start'};
`;

const MessageBubble = styled.div<{ $speaker: 'user' | 'agent' }>`
  max-width: 80%;
  padding: 10px 14px;
  border-radius: 12px;
  background: ${props => props.$speaker === 'user' 
    ? theme.colors.primary 
    : theme.colors.glass.backgroundStrong};
  border: 1px solid ${props => props.$speaker === 'user'
    ? theme.colors.secondary
    : theme.colors.glass.borderStrong};
`;

const MessageText = styled.div`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text.primary};
  line-height: 1.4;
`;

const MessageTime = styled.div`
  font-size: ${theme.typography.sizes.xs};
  color: ${theme.colors.text.muted};
  margin-top: 4px;
`;

const LiveMessage = styled(MessageBubble)`
  background: ${theme.colors.accent};
  border: 1px solid ${theme.colors.secondary};
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 8px;
    right: 8px;
    width: 6px;
    height: 6px;
    background: ${theme.colors.text.accent};
    border-radius: 50%;
    animation: blink 1s ease-in-out infinite;
  }
  
  @keyframes blink {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.3; }
  }
`;

const Footer = styled.div`
  padding: 12px 20px;
  border-top: 1px solid ${theme.colors.glass.border};
  background: ${theme.colors.glass.background};
`;

const CopyButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: ${theme.colors.glass.backgroundStrong};
  border: 1px solid ${theme.colors.glass.borderStrong};
  border-radius: ${theme.borderRadius.sm};
  color: ${theme.colors.text.primary};
  font-size: ${theme.typography.sizes.xs};
  cursor: pointer;
  transition: all ${theme.transitions.fast};
  font-family: ${theme.typography.fontFamily};
  
  &:hover {
    background: ${theme.colors.glass.backgroundStrong};
    transform: translateY(-1px);
    box-shadow: ${theme.shadows.sm};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

interface TranscriptionEntry {
  speaker: 'user' | 'agent';
  text: string;
  timestamp: Date;
}

interface LiveTranscriptionProps {
  liveTranscript: string;
  liveTranscriptSpeaker: 'user' | 'agent';
  conversationHistory: TranscriptionEntry[];
  isUserSpeaking: boolean;
  isAgentTalking: boolean;
  className?: string;
}

export const LiveTranscription: React.FC<LiveTranscriptionProps> = ({
  liveTranscript,
  liveTranscriptSpeaker,
  conversationHistory,
  isUserSpeaking,
  isAgentTalking,
  className = ''
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [conversationHistory, liveTranscript]);

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit', 
      second: '2-digit' 
    });
  };
  
  const handleCopyTranscript = async () => {
    const transcript = conversationHistory
      .map(entry => `[${formatTime(entry.timestamp)}] ${entry.speaker.toUpperCase()}: ${entry.text}`)
      .join('\n');
    
    const success = await copyToClipboard(transcript);
    if (success) {
      alert('Transcript copied to clipboard!');
    } else {
      alert('Failed to copy transcript. Please try selecting and copying the text manually.');
    }
  };

  return (
    <Container className={className}>
      <Header>
        <HeaderContent>
          <Title>Live Transcription</Title>
          <StatusIndicators>
            {isUserSpeaking && (
              <StatusIndicator>
                <StatusDot $color="#1e3a8a" />
                <span>You're speaking</span>
              </StatusIndicator>
            )}
            {isAgentTalking && (
              <StatusIndicator>
                <StatusDot $color="#2196F3" />
                <span>AI speaking</span>
              </StatusIndicator>
            )}
          </StatusIndicators>
        </HeaderContent>
      </Header>

      <TranscriptArea ref={scrollRef}>
        {conversationHistory.length === 0 && !liveTranscript && (
          <EmptyMessage>
            Start speaking to see live transcription...
          </EmptyMessage>
        )}

        {conversationHistory.map((entry, index) => (
          <TranscriptEntry key={index} $speaker={entry.speaker}>
            <MessageBubble $speaker={entry.speaker}>
              <MessageText>{entry.text}</MessageText>
              <MessageTime>{formatTime(entry.timestamp)}</MessageTime>
            </MessageBubble>
          </TranscriptEntry>
        ))}

        {/* Live Transcript (what's being spoken right now) */}
        {liveTranscript && (
          <TranscriptEntry $speaker={liveTranscriptSpeaker}>
            <LiveMessage $speaker={liveTranscriptSpeaker}>
              <MessageText>{liveTranscript}</MessageText>
              <MessageTime>Speaking...</MessageTime>
            </LiveMessage>
          </TranscriptEntry>
        )}
      </TranscriptArea>

      <Footer>
        <CopyButton 
          onClick={handleCopyTranscript}
          disabled={conversationHistory.length === 0}
        >
          <Copy size={14} />
          Copy Transcript
        </CopyButton>
      </Footer>
    </Container>
  );
};
